import React, { forwardRef, useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import { NumericFormat } from 'react-number-format';
import { SxProps, Theme, Typography } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { isEmpty } from '../../utils/lodash';
import { brandModalOutlinedInputSx } from '../../pages/BrandDashboard/brandDashboardTokens';

// Use supported currency here from globalConfig
export const currencies = [{ value: 'USD', label: '$', country: 'United States' }];

interface ISupportedCurrency {
    value: string;
    label: string;
    country: string;
}
export interface CurrencyInputProps {
    onChange: (data: { currency: string; value: number | null; displayValue?: string }) => void;
    value: number;
    details?: any;
    sx?: SxProps<Theme>;
    minAmount?: number;
    message?: string;
    supportedCurrency?: ISupportedCurrency[];
    initialCurrency?: string;
    initialValue?: number;
    /** Brand system forms — borderless field + gold focus */
    brandChrome?: boolean;
}

const ForwardedInputNumberFormat = forwardRef<HTMLInputElement, any>((props, ref) => {
    return <NumericFormat {...props} />;
});
const CurrencyTextInput: React.FC<CurrencyInputProps> = ({
    onChange,
    value = 0,
    sx,
    minAmount = 0,
    message,
    details,
    supportedCurrency = currencies,
    initialCurrency,
    initialValue,
    brandChrome = false,
}) => {
    const [currency, setCurrency] = useState('USD');
    const [amount, setAmount] = useState<number | null>(value);
    const [error, setError] = useState(false);
    const [helperText, setHelperText] = useState('');
    const { themeStyle } = useThemeContext();

    const handleCurrencyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newCurrency = event.target.value;
        setCurrency(newCurrency);
        onChange({ currency: newCurrency, value: amount });
    };

    const handleAmountChange = (value: string) => {
        // Allow digits and at most one dot
        const cleanedValue = value.replace(/[^0-9.]/g, '');

        // Prevent multiple dots
        const numericString =
            cleanedValue.split('.').length > 2
                ? cleanedValue.replace(/\.+$/, '') // remove trailing dots
                : cleanedValue;

        const parsedAmount = parseFloat(numericString);
        const isValidNumber = !isNaN(parsedAmount);

        setAmount(isValidNumber ? parsedAmount : null);
        setError(false);
        setHelperText('');

        onChange({
            currency,
            value: isValidNumber ? parsedAmount : null,
            displayValue: value,
        });
    };

    const handleBlur = () => {
        if (amount !== null && amount < minAmount / 100) {
            setError(true);
            setHelperText(`Minimum amount is ${minAmount / 100}`);
            setAmount(0);
            onChange({ currency, value: 0, displayValue: '0' });
        }
    };

    useEffect(() => {
        if (initialCurrency) {
            setCurrency(initialCurrency);
        }
        if (initialValue) {
            setAmount(initialValue);
        }
    }, [initialValue, initialCurrency]);

    useEffect(() => {
        if (details && !isEmpty(details) && Array.isArray(details)) {
            details.forEach(detail => {
                if (typeof detail === 'string') {
                    setCurrency(detail);
                }
                if (typeof detail === 'number') {
                    setAmount(detail);
                }
            });
        }
    }, [details]);

    return (
        <>
            <TextField
                variant="outlined"
                value={amount !== null ? amount.toLocaleString() : ''}
                onChange={e => handleAmountChange(e.target.value)}
                onBlur={handleBlur}
                error={error}
                helperText={helperText}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <TextField
                                select
                                value={currency}
                                onChange={handleCurrencyChange}
                                variant="standard"
                                InputProps={{
                                    disableUnderline: true,
                                    style: {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                                SelectProps={{
                                    sx: {
                                        color: theme[themeStyle].primary_white,

                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    },
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                bgcolor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].tag_white,
                                                marginTop: 0,
                                                '& .MuiMenuItem-root': {
                                                    padding: 2,
                                                },
                                            },
                                        },
                                    },
                                }}
                                sx={{
                                    minWidth: 70,
                                    backgroundColor: theme[themeStyle].gray_text,
                                    ...(brandChrome && {
                                        '& .MuiInputBase-root': {
                                            backgroundColor: theme[themeStyle].placeholderGray,
                                            borderRadius: 2,
                                        },
                                        '& .MuiSelect-select.MuiSelect-standard': {
                                            backgroundColor: '#262626',
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }),
                                }}>
                                {supportedCurrency.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {`${option.label} - ${option.country}`}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </InputAdornment>
                    ),
                    inputComponent: ForwardedInputNumberFormat as any,
                    inputProps: {
                        thousandSeparator: true,
                        prefix: `${supportedCurrency.find(c => c.value === currency)?.label || ''}`,
                    },
                    style: {
                        color: theme[themeStyle].lightWhite,
                    },
                }}
                placeholder="Enter amount"
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    ...(brandChrome
                        ? ({ ...(brandModalOutlinedInputSx as object), ...(sx as object) } as Record<string, unknown>)
                        : {
                              backgroundColor: theme[themeStyle].gray_text,
                              '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                      borderColor: theme[themeStyle].grey,
                                  },
                                  '&:hover fieldset': {
                                      borderColor: theme[themeStyle].tag_white,
                                  },
                              },
                              '& .MuiOutlinedInput-input': {
                                  paddingLeft: '14px',
                                  paddingRight: '14px',
                              },
                              ...sx,
                          }),
                }}
            />
            {message && (
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 8, color: theme[themeStyle].grey }}>
                    {message}
                </Typography>
            )}
        </>
    );
};

export { CurrencyTextInput };
